/* eslint-disable react/jsx-max-depth */
import { useMemo, useState } from "react";

import { ShareBarCell, Table } from "@similarweb/pro-logic-components";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Card, Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import type { Data } from "app/modules/api-client/queryTypes";
import { getFaviconUrl } from "app/services/sitesService/sitesUtils";
import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useDisplayAdvertiserPublisherDataAndParams } from "../../hooks/useDisplayAdvertiserPublisherDataAndParams";
import { useDurationSubtitleFilter } from "../../hooks/useDurationSubtitleFilter";
import { getFieldsForSwitcherOption } from "../../shared/hooks/useSwitcherLogic";
import { ShareTypeSwitcher, SwitcherOption } from "../../shared/ShareTypeSwitcher";
import { BottomBorderHider } from "../../StyledComponents";
import { NA_VALUE } from "../../utils/utils";
import { formatBreakdownConfig } from "../configs/breakdownConfigs";
import { useFormatBreakdown } from "../hooks/useFormatBreakdown";
import type { IFormatShare } from "../hooks/useFormatBreakdown";
import { getEmptyState } from "../PerformanceWidget";
import { CompareTableWrapper } from "../StyledComponents";
import { normalizePercentages } from "../utils/graphUtils";
import { calculateCompareEmptyStateHeight } from "../utils/utils";
import type { Translate } from "@similarweb/i18n-provider";

type FormatDataResponse = Data<"AdIntelligenceMarketingAssets", "adIntelligenceAdvertiserFormatDataList">;
type FormatDataItem = FormatDataResponse["data"][number];

const columnConfig = (translate: Translate): ColumnsType => [
    {
        dataIndex: "advertiser",
        width: 160,
        title: () => translate("ad.intelligence.advertiser.overview.breakdown.widget.domain.title"),
        render: (advertiser: string) => {
            const favicon = getFaviconUrl(advertiser);
            const internalLink = displayAdvertisingNavigation.overview.getLink({
                ...proNavigator.getParams(),
                key: advertiser,
            });

            return (
                <CoreWebsiteCell
                    domain={advertiser}
                    icon={favicon}
                    textAndIconMargin="8px"
                    showToolTip
                    internalLink={internalLink}
                    childrenCount={0}
                    spacingDomainAndAddons={false}
                    TooltipComponent={({ children }) => (
                        <Tooltip
                            content={translate(
                                "ad.intelligence.display.advertiser.ad.placements.advertiser.cell.tooltip",
                                {
                                    site: advertiser,
                                },
                            )}
                            placement="top"
                        >
                            {children}
                        </Tooltip>
                    )}
                />
            );
        },
    },
    {
        dataIndex: "formatData",
        get title() {
            return translate("ad.intelligence.overview.format.breakdown.format.title");
        },
        render: (formatShare: IFormatShare) => {
            const rawValues = [formatShare?.video || 0, formatShare?.banner || 0, formatShare?.native || 0];
            const normalizedValues = normalizePercentages(rawValues);

            return normalizedValues.every((val) => !val) ? (
                <Flex justifyContent="center">{NA_VALUE}</Flex>
            ) : (
                <Flex>
                    <ShareBarCell
                        values={normalizedValues}
                        colorSet={formatBreakdownConfig.map((config) => config.color)}
                        tooltip={{
                            get title() {
                                return translate("ad.intelligence.overview.format.breakdown.format.title");
                            },
                            labels: formatBreakdownConfig.map((config) => translate(config.translationKey)),
                            valueFormatter: (value: number) =>
                                !value ? NA_VALUE : (percentageSignFilter()(value, 1, NA_VALUE) as string),
                        }}
                        labelThreshold={0.13}
                    />
                </Flex>
            );
        },
    },
];

export const FormatBreakdownCompare = () => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const [selectedSwitcherOption, setSelectedSwitcherOption] = useState<SwitcherOption>(SwitcherOption.Spend);
    const { data, isPending, error } = useFormatBreakdown();

    const durationSubtitleFilter = useDurationSubtitleFilter(data?.startDate);
    const dateSubtitleFilters = useSubtitleFilters("date");
    const subtitleFilters = data?.isMixedDuration ? [durationSubtitleFilter] : dateSubtitleFilters;
    const keyArray = useMemo(() => (params?.key ?? "").split(","), [params?.key]);

    const tableColumns = useMemo(() => columnConfig(translate), []);

    const dataSorted = useMemo(() => {
        if (!data?.data) {
            return [];
        }

        const dataField = getFieldsForSwitcherOption(selectedSwitcherOption, true).dataField;

        return [...data.data]
            .sort((a, b) => {
                const indexA = keyArray.indexOf(a.advertiser);
                const indexB = keyArray.indexOf(b.advertiser);
                return indexA - indexB;
            })
            .map((item: FormatDataItem) => {
                const formatDataValue = item[dataField as keyof FormatDataItem] as IFormatShare | undefined;
                return {
                    advertiser: item.advertiser,
                    formatData: formatDataValue,
                };
            });
    }, [data?.data, selectedSwitcherOption, keyArray]);

    const competitorCount = keyArray.length || 0;
    const calculatedHeight = calculateCompareEmptyStateHeight(competitorCount);

    const renderEmptyState = getEmptyState(
        !!error,
        true,
        calculatedHeight,
        "ad.intelligence.overview.widget.empty.state.short.subtitle",
    );

    return (
        <Card
            background={colorsPalettes.carbon[0]}
            margin="0 0 24px"
            maxHeight="377px"
            overflow="hidden"
            position="relative"
        >
            <CompareTableWrapper>
                <Table
                    isLoading={isPending}
                    loaderRowCount={competitorCount}
                    data={dataSorted}
                    columnConfig={tableColumns}
                    noData={{ Component: () => renderEmptyState }}
                    theme="white"
                    bordersConfig={{ body: false, header: false }}
                >
                    <Table.TableTop withoutRowBorders>
                        <Table.TableTop.Title
                            title={translate(`ad.intelligence.overview.format.breakdown.widget.title`)}
                            tooltip={translate(`ad.intelligence.overview.format.breakdown.widget.title.tooltip`)}
                            filters={subtitleFilters}
                        />
                        {!isPending ? (
                            <Flex margin="0 0 12px 24px">
                                <ShareTypeSwitcher
                                    selectedSwitcherOption={selectedSwitcherOption}
                                    setSelectedSwitcherOption={setSelectedSwitcherOption}
                                />
                            </Flex>
                        ) : (
                            <span />
                        )}
                    </Table.TableTop>
                </Table>
            </CompareTableWrapper>
            <BottomBorderHider />
        </Card>
    );
};
